import type React from 'react'
import { useState, useEffect } from 'react'
import { Drawer, message, Alert, Form, Button } from 'antd'
import { Vertical, GraviButton, Texto, Horizontal } from '@gravitate-js/excalibrr'
import { SaveOutlined } from '@ant-design/icons'
import { type GlobalConfig, useSupplierReconciliation } from '@api/useSupplierReconciliation'
import { UnifiedConfigTable } from './UnifiedConfigTable/index'
import { PopconfirmButton } from '../../../../_SiteWide/PopconfirmButton'

interface GlobalConfigDrawerProps {
  visible: boolean
  onClose: () => void
  globalConfig?: GlobalConfig
}

/**
 * Global Configuration Drawer with Unified Table View
 * Shows all configuration options in a single tabular view
 */
export function GlobalConfigDrawer({ visible, onClose, globalConfig }: GlobalConfigDrawerProps) {
  const [form] = Form.useForm()
  const [isDirty, setIsDirty] = useState(false)

  // Reset form when globalConfig changes or drawer becomes visible
  useEffect(() => {
    if (globalConfig && visible) {
      form.resetFields()
      form.setFieldsValue(globalConfig)
      setIsDirty(false)
    }
  }, [globalConfig, visible, form])

  const { useUpdateDefaultConfig } = useSupplierReconciliation()

  const { mutateAsync: updateGlobalConfig, isPending } = useUpdateDefaultConfig()

  // Handle save
  const handleSave = async (values: GlobalConfig) => {
    try {
      await updateGlobalConfig({
        body: {
          auto_match_rules: values.auto_match_rules,
          auto_approve_rules: values.auto_approve_rules,
          warning_rules: values.warning_rules,
          assignee: null, // Global config doesn't have assignee
        },
      })

      message.success('Global configuration saved successfully')
      setIsDirty(false)
      onClose()
    } catch (error) {
      console.error('Failed to save global configuration:', error)
      message.error('Failed to save global configuration')
    }
  }

  // Handle drawer close
  const handleDrawerClose = () => {
    // Reset form to original values and clear dirty state
    if (globalConfig) {
      form.resetFields()
      form.setFieldsValue(globalConfig)
    }
    setIsDirty(false)
    onClose()
  }

  if (!globalConfig) return null

  return (
    <Drawer
      data-testid='global-config-drawer'
      title={
        <Vertical gap={0}>
          <Texto weight='bold' category='h5'>
            Global Configuration
          </Texto>
          <Texto appearance='hint' category='p1'>
            These settings apply to all suppliers by default. Individual supplier settings will override these global
            defaults.
          </Texto>
        </Vertical>
      }
      width={600}
      placement='right'
      onClose={handleDrawerClose}
      open={visible}
      maskClosable={!isDirty}
      destroyOnClose={true}
      footer={
        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton onClick={handleDrawerClose}>Cancel</GraviButton>
          <PopconfirmButton
            showAlert={true}
            onConfirm={async () => {
              try {
                const values = await form.validateFields()
                await handleSave(values)
              } catch (error) {
                // Form validation failed, error is already shown by antd
              }
            }}
            buttonProps={{
              theme2: true,
              buttonText: 'Save Changes',
              icon: <SaveOutlined />,
              loading: isPending,
              disabled: !isDirty,
            }}
            popconfirmProps={{
              title: 'Save Global Configuration?',
              description:
                'This will update the default configuration for all suppliers. Existing supplier-specific configurations will not be affected.',
              okText: 'Yes, Save',
              cancelText: 'Cancel',
              okButtonProps: {
                'data-testid': 'global-config-confirm-save',
              },
            }}
          />
        </Horizontal>
      }
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Form form={form} initialValues={globalConfig} name='globalConfig' onFinish={handleSave} layout='vertical'>
        <Vertical gap={16} style={{ height: '100%', overflow: 'auto' }}>
          <UnifiedConfigTable form={form} onFormChange={() => setIsDirty(true)} />
        </Vertical>
      </Form>
    </Drawer>
  )
}
